import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'

import { EmptySearchResult } from '@/components'
import type { TableColumn } from '@/components/Table'
import { Table } from '@/components/Table'
import TablePagination from '@/components/Table/TablePagination'
import { Input } from '@/components/ui/input'
import { FileValidationStatus } from '@/schema'
import { formatNumber } from '@/utils'
import { TABLE_PAGE_SIZE } from '@/utils/constants'

import { FileUploadAction, getFileUploadColumns } from '../columns'
import { useFileUploadFiles, useFileUploadStore } from '../store'
import { UploadFile, UploadPhase } from '../types'

import { AttachedFilesEmpty } from './AttachedFilesEmpty'
import { FileStatusTabs } from './FileStatusTabs'

export const AttachedFilesTable = () => {
  const { t } = useTranslation()
  const files = useFileUploadFiles()
  const {
    filters,
    uploadPhase,
    removeFile,
    cancelFileValidation,
    updateFileType,
    setFilter,
  } = useFileUploadStore()

  const [searchText, setSearchText] = useState(filters.searchText)
  const [page, setPage] = useState(0)

  const handleSearchChange = (value: string) => {
    setSearchText(value)
    setFilter({ searchText: value })
    setPage(0)
  }

  const handleActionClick = useCallback(
    (action: FileUploadAction, file: UploadFile) => {
      switch (action) {
        case 'delete':
          removeFile(file.id)
          break
        case 'cancel':
          cancelFileValidation(file.id)
          break
        default:
          break
      }
    },
    [removeFile, cancelFileValidation]
  )

  const handleFileTypeChange = useCallback(
    (fileId: string, documentType: string) => {
      updateFileType(fileId, documentType)
    },
    [updateFileType]
  )

  const filteredFiles = useMemo(() => {
    const { searchText, status } = filters
    let result = files

    // Only apply searchText filter if present and non-empty
    if (searchText && searchText.trim() !== '') {
      const search = searchText.toLowerCase()
      result = result.filter((file) =>
        file.fileName.toLowerCase().includes(search)
      )
    }

    // Only apply status filter if not 'all'
    if (status !== 'all') {
      // Optimize with switch-case for filtering by status
      if (status === FileValidationStatus.ACTION_REQUIRED) {
        result = result.filter((file) => file.isActionRequired)
      } else {
        result = result.filter((file) => file.validationStatus === status)
      }
    }

    // Order the result: Validating, Action Required, Failed, Ready to upload, Canceled
    const priority: Record<string, number> = {
      [FileValidationStatus.VALIDATING]: 0,
      [FileValidationStatus.ACTION_REQUIRED]: 1,
      [FileValidationStatus.FAILED]: 2,
      [FileValidationStatus.READY_TO_UPLOAD]: 3,
      [FileValidationStatus.CANCELLED]: 4,
    }

    // Helper function to get the effective status for a file
    const getStatus = (file: UploadFile) => {
      if (
        file.validationStatus === FileValidationStatus.ACTION_REQUIRED ||
        file.isActionRequired
      ) {
        // Use isActionRequired as possible flag for Action Required
        return FileValidationStatus.ACTION_REQUIRED
      }
      return file.validationStatus
    }

    // Helper function to compare strings (handles undefined/null)
    const compareStrings = (a: string = '', b: string = ''): number => {
      return a.localeCompare(b, undefined, { sensitivity: 'base' })
    }

    // Sort with primary sort by status priority, secondary sort by errorMessage (for FAILED) and fileName
    return result.slice().sort((a, b) => {
      const aStatus = getStatus(a)
      const bStatus = getStatus(b)
      const aPriority = priority[aStatus] ?? 99
      const bPriority = priority[bStatus] ?? 99

      // Primary sort: by status priority
      if (aPriority !== bPriority) {
        return aPriority - bPriority
      }

      // Secondary sort: within the same status group
      // For FAILED status: sort by errorMessage first, then by fileName
      if (
        aStatus === FileValidationStatus.FAILED &&
        bStatus === FileValidationStatus.FAILED
      ) {
        const errorMessageCompare = compareStrings(
          a.errorMessage,
          b.errorMessage
        )
        if (errorMessageCompare !== 0) {
          return errorMessageCompare
        }
      }

      // For all statuses (including FAILED after errorMessage comparison): sort by fileName
      return compareStrings(a.fileName, b.fileName)
    })
  }, [files, filters])

  // Pagination logic - zero-based page index
  const paginatedFiles = useMemo(() => {
    const startIndex = page * TABLE_PAGE_SIZE
    const endIndex = startIndex + TABLE_PAGE_SIZE
    return filteredFiles.slice(startIndex, endIndex)
  }, [filteredFiles, page])

  const totalPages = useMemo(
    () => Math.ceil(filteredFiles.length / TABLE_PAGE_SIZE),
    [filteredFiles]
  )

  useEffect(() => {
    setPage(0)
  }, [filters.status])

  useEffect(() => {
    if (totalPages > 0 && page >= totalPages) {
      setPage(totalPages - 1)
    }
  }, [page, totalPages])

  const columns: TableColumn<UploadFile>[] = useMemo(
    () =>
      getFileUploadColumns(
        handleActionClick,
        handleFileTypeChange,
        t,
        uploadPhase
      ),
    [handleActionClick, handleFileTypeChange, t, uploadPhase]
  )

  const dynamicDescription = useMemo(() => {
    const validatingCount = files.filter(
      (f) => f.validationStatus === FileValidationStatus.VALIDATING
    ).length
    const uploadingCount = files.filter(
      (f) => f.validationStatus === FileValidationStatus.UPLOADING
    ).length
    const uploadedCount = files.filter(
      (f) => f.validationStatus === FileValidationStatus.UPLOADED
    ).length

    switch (uploadPhase) {
      case UploadPhase.UNZIPPING:
        return {
          text: t('fileUpload.attachedFiles.descriptionUnzipping'),
        }
      case UploadPhase.VALIDATION: {
        if (validatingCount > 0) {
          return {
            text: t('fileUpload.attachedFiles.descriptionValidating', {
              validating: formatNumber(files.length - validatingCount),
              total: formatNumber(files.length),
              count: files.length,
            }),
            color: 'text-tertiary-600',
          }
        }
        if (files.length > 0) {
          return {
            text: t('fileUpload.attachedFiles.descriptionValidated'),
            color: 'text-[--Colors-Success-600]',
          }
        }
        return { text: undefined, color: 'text-tertiary-600' }
      }
      case UploadPhase.UPLOAD: {
        if (uploadingCount > 0) {
          return {
            text: t('fileUpload.attachedFiles.descriptionUploading', {
              uploading: formatNumber(uploadedCount),
              total: formatNumber(files.length),
              count: files.length,
            }),
            color: 'text-tertiary-600',
          }
        }
        if (uploadedCount === files.length) {
          return {
            text: t('fileUpload.attachedFiles.descriptionUploaded', {
              total: formatNumber(files.length),
              count: files.length,
            }),
            color: 'text-tertiary-600',
          }
        }

        return { text: undefined, color: 'text-tertiary-600' }
      }

      default:
        return { text: undefined, color: 'text-tertiary-600' }
    }
  }, [files, uploadPhase, t])

  const hasFiles = useMemo(() => files.length > 0, [files])

  return (
    <div className="w-full">
      <div className="flex flex-col gap-2 pt-6 pb-5 pl-8 border border-b-0 border-solid border-gray-light-200 rounded-t-xl">
        <h2 className="m-0 text-lg font-semibold text-primary-900">
          {t('fileUpload.attachedFiles.title')}
        </h2>
        {dynamicDescription.text && (
          <p className={`m-0 text-sm ${dynamicDescription.color}`}>
            {dynamicDescription.text}
          </p>
        )}
      </div>

      <div className="w-full">
        {hasFiles ? (
          <div className="w-full">
            <div className="flex justify-between gap-4 px-6 py-4 bg-white border border-b-0 border-solid border-gray-light-200 sm:flex-row sm:items-center">
              <div>
                <FileStatusTabs />
              </div>
              <div>
                <Input
                  type="text"
                  leadingIcon={<Search size={20} className="text-gray-400 " />}
                  placeholder={t('project.filters.searchPlaceholder')}
                  value={searchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  autoComplete="off"
                  trailingIcon={
                    searchText ? (
                      <X className="w-3.5 h-3.5 cursor-pointer" />
                    ) : undefined
                  }
                  onTrailingIconClick={
                    searchText ? () => setSearchText('') : undefined
                  }
                />
              </div>
            </div>

            <div className="w-full overflow-x-auto">
              <Table
                columns={columns}
                data={paginatedFiles}
                className="border border-gray-light-200"
                roundedBottom
                noDataMessage={
                  searchText ? (
                    <EmptySearchResult translationKey="fileUpload.attachedFiles" />
                  ) : (
                    <AttachedFilesEmpty />
                  )
                }
              />
            </div>

            {totalPages > 1 && (
              <div className="flex justify-end bg-white border border-t-0 border-solid rounded-b-xl border-gray-light-200">
                <TablePagination
                  page={page}
                  totalPages={totalPages}
                  setPage={setPage}
                />
              </div>
            )}
          </div>
        ) : (
          <AttachedFilesEmpty />
        )}
      </div>
    </div>
  )
}
